import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import { usePermissions } from "../../login/PermissionContext";
import { fetchPatientRisRecords } from "../../billing-payment/utils/risIntegration";
import { fetchPatientLisRecords } from "../../billing-payment/utils/lisIntegration";

// RIS (radiology images) and LIS (lab report) records of the patient, one
// table. Each source is read only when the facility has it integrated.
const RadiologyReports = ({ patientId }) => {
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  const { userInfo } = usePermissions();
  const risOn = Boolean(userInfo?.ris_integrated);
  const lisOn = Boolean(userInfo?.lis_integrated);

  const authToken = sessionStorage.getItem("accessToken") || "";

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${authToken}`,
    }),
    [authToken]
  );

  useEffect(() => {
    const fetchReports = async () => {
      if (!patientId || (!risOn && !lisOn)) {
        setReports([]);
        return;
      }

      // One source failing must not hide the other's reports.
      const load = (enabled, fetchRecords, label) =>
        enabled
          ? fetchRecords(patientId, headers).catch((error) => {
              console.error(`Error fetching ${label} reports:`, error);
              toast.error(
                error?.response?.data?.message ||
                  `Unable to fetch ${label} reports at the moment.`
              );
              return [];
            })
          : [];

      setIsLoading(true);
      const [risRecords, lisRecords] = await Promise.all([
        load(risOn, fetchPatientRisRecords, "radiology"),
        load(lisOn, fetchPatientLisRecords, "lab"),
      ]);
      setReports([
        ...risRecords.map((r) => ({
          ...r,
          kind: "Radiology",
          reportUrl: r?.diacom_url || r?.dicom_url,
        })),
        ...lisRecords.map((r) => ({ ...r, kind: "Lab", reportUrl: r?.test_report_url })),
      ]);
      setIsLoading(false);
    };

    fetchReports();
  }, [patientId, headers, risOn, lisOn]);

  const rows = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    return reports
      .map((item, index) => ({
        ...item,
        // RIS and LIS rows come from two tables, so their ids can collide.
        id: `${item.kind}-${
          item?.id ||
          `${item?.carecontext_id || "cc"}-${item?.medical_service_id || "ms"}-${
            item?.registration_date || index
          }-${index}`
        }`,
      }))
      .filter((row) => {
        if (!normalizedSearch) return true;
        return (
          String(row?.carecontext_id || "")
            .toLowerCase()
            .includes(normalizedSearch) ||
          String(row?.medical_service_id || "")
            .toLowerCase()
            .includes(normalizedSearch) ||
          String(row?.medical_service_name || "")
            .toLowerCase()
            .includes(normalizedSearch) ||
          String(row?.registration_date || "")
            .toLowerCase()
            .includes(normalizedSearch)
        );
      });
  }, [reports, searchText]);

  // The TeleRad viewer sends `frame-ancestors 'self'` and a SameSite=Strict
  // login cookie, so browsers refuse to render it inside an iframe on our
  // origin. It has to open in its own tab.
  const openViewer = useCallback((row) => {
    if (!row?.reportUrl) {
      toast.info(
        row?.kind === "Lab"
          ? "Lab report is not available yet."
          : "Radiology report is not available yet."
      );
      return;
    }
    window.open(row.reportUrl, "_blank", "noopener,noreferrer");
  }, []);

  const columns = useMemo(
    () => [
      {
        field: "carecontext_id",
        headerName: "Care context",
        minWidth: 180,
        flex: 1,
        sortable: true,
      },
      {
        field: "medical_service_name",
        headerName: "Medical Service Name",
        minWidth: 220,
        flex: 1,
        sortable: true,
      },
      {
        field: "registration_date",
        headerName: "Date",
        minWidth: 160,
        flex: 1,
        sortable: true,
        valueFormatter: (params) => {
          if (!params?.value) return "-";
          return params.value;
        },
      },
      {
        field: "view",
        headerName: "View",
        minWidth: 140,
        flex: 0.5,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Typography
            component="span"
            onClick={() => openViewer(params?.row)}
            sx={{
              color: "#176BAD",
              textDecoration: "underline",
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: 500,
            }}
          >
            Click to View
          </Typography>
        ),
      },
    ],
    [openViewer]
  );

  return (
    <Box sx={{ pt: 1 }}>
      <Paper
        sx={{
          border: "1px solid #176BAD",
          borderRadius: "10px",
          boxShadow: "0px 1px 3px rgba(0,0,0,0.1)",
          p: 1.5,
        }}
      >
        <TextField
          size="small"
          placeholder="Search by care context, medical service or date"
          fullWidth
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ mb: 1.5 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ height: 500, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={isLoading}
            pageSizeOptions={[5, 10, 20]}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
              sorting: {
                sortModel: [{ field: "registration_date", sort: "desc" }],
              },
            }}
            disableRowSelectionOnClick
            sx={{
              "&.MuiDataGrid-root": {
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                fontFamily: "Arial, sans-serif",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#125386",
                color: "#ffffff",
                borderBottom: "1px solid #D9D9D9",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "#ffffff",
                fontWeight: 600,
                fontSize: "13px",
              },
              "& .MuiDataGrid-columnSeparator": {
                color: "rgba(255,255,255,0.35)",
              },
              "& .MuiSvgIcon-root": {
                color: "#ffffff",
              },
              "& .MuiDataGrid-row": {
                borderBottom: "1px solid #e0e0e0",
                "&:hover": {
                  backgroundColor: "#f8f9fa",
                },
              },
              "& .MuiDataGrid-cell": {
                fontSize: "12px",
                color: "#374151",
                borderRight: "1px solid #e0e0e0",
                "&:last-child": {
                  borderRight: "none",
                },
                "&:focus": {
                  outline: "none",
                },
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "1px solid #e0e0e0",
                backgroundColor: "#f8f9fa",
              },
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default RadiologyReports;
